import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useMemo, useState } from "react";

import { useDefinitions } from "../components/OutreachDefinitions";
import OutreachFunnel, { type Funnel } from "../components/OutreachFunnel";
import OutreachSourceTrend, { type TrendPoint } from "../components/OutreachSourceTrend";
import PageHeader from "../components/PageHeader";
import {
  OUTREACH_BY_SOURCE,
  OUTREACH_RESULT_MEMBERS,
  OUTREACH_RESULTS,
} from "../gql/queries/outreachResults";
import {
  type BreakdownKey,
  humanize,
  metaFor,
  SOURCE_ORDER,
  type SourceMeta,
} from "../helpers/outreachSources";
import { Spacing } from "../types/enum";

/**
 * Outreach Impact — did each tool do what it exists for?
 *
 * Built around one question a Community Manager actually asks: we did all this
 * work, what came of it? So the screen is organised by tool, because that's how
 * the work is organised, and every tool is judged against its own goal rather
 * than a single shared definition of success. Reaching a cancelling member is
 * about whether she replied and stayed; inviting someone to a welcome event is
 * about whether she came. One number can't mean both.
 *
 * Three layers, in the order the question gets asked:
 *
 *  1. **The board** — every tool side by side, each with the one number that is
 *     its goal, and how that number moved against the period before.
 *  2. **A panel** — open a tool for its full funnel, its trend, and the slices
 *     underneath (cycles, goals, cohort filters).
 *  3. **The members** — the people behind any number, so a figure can always be
 *     turned back into names.
 *
 * The period drives everything and compares itself with the equivalent period
 * before, so "how did June go" and "how are the last 14 days" are the same
 * question asked with different dates.
 *
 * What each tool is *for* lives in `helpers/outreachSources`, not here — the
 * screen renders goals, it doesn't decide them.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

interface Period {
  key: string;
  label: string;
  from: number;
  to: number;
  covers: string;
}

/**
 * Rolling windows for "how are we doing lately", whole calendar months for "how
 * did June go". Every one of them is compared against the equally long period
 * immediately before it, which is why there's no All time: nothing precedes it.
 */
const buildPeriods = (): Period[] => {
  const now = new Date();
  const rolling = [14, 30, 90].map((days) => ({
    key: `last-${days}`,
    label: `Last ${days} days`,
    from: now.getTime() - days * DAY_MS,
    to: now.getTime(),
    covers: `compared with the ${days} days before that`,
  }));

  const months: Period[] = [];
  for (let back = 0; back < 8; back += 1) {
    const start = new Date(now.getFullYear(), now.getMonth() - back, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - back + 1, 1);
    months.push({
      key: `month-${format(start, "yyyy-MM")}`,
      label: format(start, "MMMM yyyy"),
      from: start.getTime(),
      to: Math.min(end.getTime(), now.getTime()),
      covers:
        back === 0
          ? "this month so far, compared with the same stretch of last month"
          : `compared with ${format(new Date(now.getFullYear(), now.getMonth() - back - 1, 1), "MMMM")}`,
    });
  }

  return [...rolling, ...months];
};

interface SourceRow extends Funnel {
  source?: string | null;
}

interface BreakdownRow extends Funnel {
  bucket: string;
}

interface MemberRow {
  userId: string;
  name: string;
  email: string;
  sends: number;
  firstAt: number;
  lastAt: number;
  campaign?: string | null;
  riskThen?: string | null;
  eventsThen?: number | null;
  responded: boolean;
  attendedAfter: number;
  attendedInvited: boolean;
  accepted: boolean;
  declined: boolean;
  loggedInSince: boolean;
  lastLoginAt?: number | null;
  endsAt?: number | null;
  membership: string;
}

const percent = (value: number, total: number): string =>
  total > 0 ? `${Math.round((value / total) * 100)}%` : "—";

/** The one number that says whether a tool met its goal, in a sentence. */
const verdict = (meta: SourceMeta, funnel: Funnel): string => {
  if (!meta.headline || funnel.reached === 0) {
    return "—";
  }
  const step = meta.steps.find((entry) => entry.key === meta.headline);
  const value = funnel[meta.headline] as number;
  return `${percent(value, funnel.reached)} ${step?.label.toLowerCase() ?? ""}`;
};

/** Her membership now, in words a person would use. */
const MEMBERSHIP_LABEL: Record<string, string> = {
  STAYED: "Still a member",
  LEAVING: "Leaving — date not reached",
  CANCELLED: "Left",
  PAUSED: "Paused",
  UNKNOWN: "No subscription",
};

const MEMBERSHIP_COLOR: Record<string, "success" | "warning" | "error" | "default"> = {
  STAYED: "success",
  LEAVING: "warning",
  CANCELLED: "error",
  PAUSED: "default",
  UNKNOWN: "default",
};

/**
 * Every tool at once — the landing view.
 *
 * A table rather than a row of cards: eleven tools is too many to scan as
 * cards, and the whole value here is comparing one line against the next.
 */
const Board: React.FC<{
  rows: SourceRow[];
  selected?: string | null;
  onSelect: (source: string) => void;
  helpButton: (term?: string) => React.ReactNode;
}> = ({ rows, selected, onSelect, helpButton }) => {
  const ordered = useMemo(() => {
    const bySource = new Map(rows.map((row) => [row.source ?? "", row]));
    return SOURCE_ORDER.map((source) => bySource.get(source)).filter((row): row is SourceRow =>
      Boolean(row),
    );
  }, [rows]);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Tool</TableCell>
          <TableCell align="right">Reached {helpButton("Reached")}</TableCell>
          <TableCell align="right">Replied {helpButton("Replied")}</TableCell>
          <TableCell align="right">
            Came to a conversation {helpButton("Came to a conversation")}
          </TableCell>
          <TableCell align="right">Still a member {helpButton("Still a member")}</TableCell>
          <TableCell>Did it meet its goal? {helpButton("Did it meet its goal?")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {ordered.map((row) => {
          const meta = metaFor(row.source);
          const key = row.source as string;
          return (
            <TableRow
              key={key}
              hover
              selected={selected === key}
              onClick={() => onSelect(key)}
              sx={{ cursor: "pointer" }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {meta.emoji} {meta.label}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {meta.goal}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={700}>
                  {row.reached}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {row.sends} sends
                </Typography>
              </TableCell>
              <TableCell align="right">{percent(row.responded, row.reached)}</TableCell>
              <TableCell align="right">{percent(row.attendedAny, row.reached)}</TableCell>
              <TableCell align="right">{percent(row.stayed, row.reached)}</TableCell>
              <TableCell>
                {meta.headline ? (
                  <Typography variant="body2" fontWeight={600}>
                    {verdict(meta, row)}
                  </Typography>
                ) : (
                  <Tooltip title={meta.note ?? ""}>
                    <Typography variant="caption" color="textSecondary">
                      No single goal
                    </Typography>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

/** The members behind a tool's numbers, loaded only once asked for. */
const MemberList: React.FC<{
  source: string;
  campaign: string | null;
  from: number;
  to: number;
}> = ({ source, campaign, from, to }) => {
  const { loading, data } = useQuery(OUTREACH_RESULT_MEMBERS, {
    variables: { source, campaign, from, to },
    fetchPolicy: "network-only",
  });
  const members: MemberRow[] = data?.adminOutreachResultMembers ?? [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={Spacing.l}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (members.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        Nobody was reached here in this period.
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Member</TableCell>
            <TableCell>Reached</TableCell>
            <TableCell>Risk then</TableCell>
            <TableCell align="right">Replied</TableCell>
            <TableCell align="right">Conversations since</TableCell>
            <TableCell>Where she is now</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.userId} hover>
              <TableCell>
                <Typography variant="body2">{member.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {member.email}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{format(new Date(member.firstAt), "d MMM")}</Typography>
                {member.campaign && (
                  <Typography variant="caption" color="textSecondary">
                    {humanize(member.campaign)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="textSecondary">
                  {humanize(member.riskThen)}
                </Typography>
              </TableCell>
              <TableCell align="right">{member.responded ? "Yes" : "—"}</TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={member.attendedAfter > 0 ? 700 : 400}>
                  {member.attendedAfter}
                </Typography>
                {member.eventsThen !== null && member.eventsThen !== undefined && (
                  <Typography variant="caption" color="textSecondary">
                    was {member.eventsThen}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={MEMBERSHIP_LABEL[member.membership] ?? member.membership}
                  color={MEMBERSHIP_COLOR[member.membership] ?? "default"}
                  variant="outlined"
                />
                {member.membership === "LEAVING" && member.endsAt && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    ends {format(new Date(member.endsAt), "d MMM")}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {members.length >= 500 && (
        <Typography variant="caption" color="textSecondary">
          Showing the 500 most recent. The numbers above cover everyone.
        </Typography>
      )}
    </Box>
  );
};

/** One tool opened: its funnel, its trend, its slices, and its members. */
const SourcePanel: React.FC<{
  source: string;
  period: Period;
  helpButton: (term?: string) => React.ReactNode;
}> = ({ source, period, helpButton }) => {
  const meta = metaFor(source);
  const [breakdown, setBreakdown] = useState<BreakdownKey>(meta.breakdowns[0]?.key ?? "CAMPAIGN");
  const [slice, setSlice] = useState<string | null>(null);
  const [showMembers, setShowMembers] = useState(false);

  // A different tool asks a different question, so its breakdown resets with it.
  React.useEffect(() => {
    setBreakdown(meta.breakdowns[0]?.key ?? "CAMPAIGN");
    setSlice(null);
    setShowMembers(false);
  }, [meta]);

  const { loading, data } = useQuery(OUTREACH_RESULTS, {
    variables: {
      source,
      from: period.from,
      to: period.to,
      breakdown,
      bucket: period.to - period.from > 100 * DAY_MS ? "month" : "week",
    },
    fetchPolicy: "network-only",
  });

  const result = data?.adminOutreachResults;
  const current: Funnel | undefined = result?.current;
  const previous: Funnel | undefined = result?.previous;
  const rows: BreakdownRow[] = result?.breakdown ?? [];
  const trend: TrendPoint[] = result?.trend ?? [];

  if (loading && !result) {
    return (
      <Box display="flex" justifyContent="center" p={Spacing.xl}>
        <CircularProgress />
      </Box>
    );
  }
  if (!current) {
    return null;
  }

  const headlineStep = meta.steps.find((step) => step.key === meta.headline);

  return (
    <Box display="flex" flexDirection="column" gap={Spacing.l}>
      <Box>
        <Typography variant="h6">
          {meta.emoji} {meta.label}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {meta.goal}
        </Typography>
      </Box>

      {meta.note && (
        <Alert severity="info" variant="outlined">
          <Typography variant="caption" color="textSecondary">
            {meta.note}
          </Typography>
        </Alert>
      )}

      {meta.headline && headlineStep && current.reached > 0 && (
        <Card variant="outlined" sx={{ p: Spacing.m }}>
          <Typography variant="caption" color="textSecondary">
            Goal — {headlineStep.hint}
            {helpButton("Did it meet its goal?")}
          </Typography>
          <Typography variant="h4" sx={{ mt: Spacing.xs }}>
            {percent(current[meta.headline] as number, current.reached)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {current[meta.headline] as number} of the {current.reached} members reached
            {previous && previous.reached > 0 && (
              <>
                {" · "}
                {percent(previous[meta.headline] as number, previous.reached)} over the period
                before
                {helpButton("▲ ▼ points")}
              </>
            )}
          </Typography>
        </Card>
      )}

      <OutreachFunnel meta={meta} current={current} previous={previous} helpButton={helpButton} />

      {meta.label === "New Members" && current.medianAttended !== null && (
        <Typography variant="body2" color="textSecondary">
          Half the new members we reached have been to{" "}
          <strong>{current.medianAttended ?? 0}</strong> conversation
          {current.medianAttended === 1 ? "" : "s"} or more since. The median, not the average, so
          one very keen member can&apos;t flatter the rest.
        </Typography>
      )}

      {trend.length > 1 && (
        <OutreachSourceTrend
          points={trend}
          metric={meta.headline === "responded" ? "responded" : "attendedAny"}
          label={meta.headline === "responded" ? "Replied" : "Came to a conversation"}
        />
      )}

      {meta.breakdowns.length > 0 && (
        <Box display="flex" flexDirection="column" gap={Spacing.s}>
          <TextField
            select
            size="small"
            label="Break it down"
            value={breakdown}
            onChange={(event) => {
              setBreakdown(event.target.value as BreakdownKey);
              setSlice(null);
            }}
            sx={{ maxWidth: 280 }}
          >
            {meta.breakdowns.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {breakdown === "FILTER" && (
            <Typography variant="caption" color="textSecondary">
              A member picked by three filters is counted under all three — this asks which filters
              we reach for, not how to divide members between them.
            </Typography>
          )}
          {(breakdown === "PRIORITY" || breakdown === "CHALLENGE") && (
            <Typography variant="caption" color="textSecondary">
              From her answers in the new signup flow. Anyone who signed up before it is counted
              under &ldquo;No answer&rdquo;.
            </Typography>
          )}

          {rows.length > 0 && (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{meta.breakdowns.find((b) => b.key === breakdown)?.label}</TableCell>
                    <TableCell align="right">Reached {helpButton("Reached")}</TableCell>
                    <TableCell align="right">Replied {helpButton("Replied")}</TableCell>
                    <TableCell align="right">Came {helpButton("Came to a conversation")}</TableCell>
                    <TableCell align="right">
                      Still a member {helpButton("Still a member")}
                    </TableCell>
                    <TableCell align="right">Left {helpButton("Left")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.bucket}
                      hover
                      selected={slice === row.bucket}
                      // Only campaign slices can be turned back into a member
                      // list — the others are computed after the fact, and a row
                      // that looks clickable but isn't is worse than a plain one.
                      onClick={() =>
                        breakdown === "CAMPAIGN" &&
                        setSlice(slice === row.bucket ? null : row.bucket)
                      }
                      sx={{ cursor: breakdown === "CAMPAIGN" ? "pointer" : "default" }}
                    >
                      <TableCell>{humanize(row.bucket)}</TableCell>
                      <TableCell align="right">{row.reached}</TableCell>
                      <TableCell align="right">{percent(row.responded, row.reached)}</TableCell>
                      <TableCell align="right">{percent(row.attendedAny, row.reached)}</TableCell>
                      <TableCell align="right">{percent(row.stayed, row.reached)}</TableCell>
                      <TableCell align="right">{percent(row.cancelled, row.reached)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      <Box>
        <Typography
          variant="body2"
          onClick={() => setShowMembers((open) => !open)}
          sx={{ cursor: "pointer", textDecoration: "underline", width: "fit-content" }}
        >
          {showMembers ? "Hide the members" : "Show the members behind these numbers"}
          {slice ? ` (${humanize(slice)})` : ""}
        </Typography>
        {showMembers && (
          <Box mt={Spacing.s}>
            <MemberList source={source} campaign={slice} from={period.from} to={period.to} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const OutreachImpactPage: React.FC = () => {
  const periods = useMemo(buildPeriods, []);
  const [periodKey, setPeriodKey] = useState(periods[1].key);
  const [source, setSource] = useState<string | null>(null);
  const definitions = useDefinitions();

  const period = periods.find((entry) => entry.key === periodKey) ?? periods[0];

  const { loading, data } = useQuery(OUTREACH_BY_SOURCE, {
    variables: { from: period.from, to: period.to },
    fetchPolicy: "network-only",
  });

  /*
   * Only outreach we can name a tool for.
   *
   * The API also returns sends from before the log recorded which screen they
   * came from. There is nothing to do with them — you can't judge a tool by work
   * you can't attribute to it — so they're dropped here rather than shown as a
   * row nobody can act on. The totals are taken after the filter, so the line
   * under the picker describes exactly the rows beneath it.
   */
  const rows: SourceRow[] = useMemo(
    () => (data?.adminOutreachBySource ?? []).filter((row: SourceRow) => Boolean(row.source)),
    [data],
  );

  const totals = useMemo(
    () =>
      rows.reduce(
        (sum, row) => ({
          reached: sum.reached + row.reached,
          sends: sum.sends + row.sends,
        }),
        { reached: 0, sends: 0 },
      ),
    [rows],
  );

  return (
    <>
      <PageHeader title="Outreach Impact" />
      {definitions.dialog}

      <Box display="flex" flexDirection="column" gap={Spacing.l} mt={Spacing.m}>
        <Alert severity="info" variant="outlined">
          <Typography variant="caption" component="p" color="textSecondary">
            Every tool measured against <strong>its own goal</strong> — saving a cancelling member
            is a different job from filling a welcome event, so they&apos;re not held to one number.
            Everything counts <strong>members, not messages</strong>, and is measured from the first
            time that tool reached her.
          </Typography>
          <Typography variant="caption" component="p" color="textSecondary" mt={Spacing.xs}>
            Every figure is compared with the{" "}
            <strong>equally long period immediately before</strong> it, so a change is a real change
            rather than a bigger send.
          </Typography>
          <Box mt={Spacing.xs}>
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="always"
              onClick={definitions.open}
            >
              What do these numbers mean?
            </Link>
          </Box>
        </Alert>

        <Box display="flex" alignItems="flex-end" gap={Spacing.m} flexWrap="wrap">
          <TextField
            select
            size="small"
            label="Period"
            value={periodKey}
            onChange={(event) => setPeriodKey(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            {periods.map((entry) => (
              <MenuItem key={entry.key} value={entry.key}>
                {entry.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="textSecondary" pb={Spacing.s}>
            {period.covers} · {totals.reached} members reached in total, from {totals.sends} sends
          </Typography>
        </Box>

        {loading && rows.length === 0 ? (
          <Box display="flex" justifyContent="center" p={Spacing.xl}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No outreach was sent in this period.
          </Typography>
        ) : (
          <Card variant="outlined" sx={{ overflowX: "auto" }}>
            <Board
              rows={rows}
              selected={source}
              onSelect={setSource}
              helpButton={definitions.helpButton}
            />
          </Card>
        )}

        {source && (
          <Card variant="outlined" sx={{ p: Spacing.l }}>
            <SourcePanel source={source} period={period} helpButton={definitions.helpButton} />
          </Card>
        )}
      </Box>
    </>
  );
};

export default OutreachImpactPage;
